import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';
import type { RemarkCreate } from '@/types/api';

interface Props {
  projectId: string;
  snapshotId: string | null;
  onClose: () => void;
}

const SECTIONS = ['ПЗ', 'АР', 'КР', 'ТКР', 'ПОС', 'ООС'] as const;
type DefectKind = 'S' | 'E';
const SEVERITY_OPTS: { id: 'Critical' | 'High' | 'Medium' | 'Low'; label: string; color: string }[] = [
  { id: 'Critical', label: 'Критическое', color: '#EF4444' },
  { id: 'High',     label: 'Высокое',     color: '#F59E0B' },
  { id: 'Medium',   label: 'Среднее',     color: '#6B7280' },
  { id: 'Low',      label: 'Низкое',      color: '#9CA3AF' },
];

export function CreateRemarkModal({ projectId, snapshotId, onClose }: Props) {
  const qc = useQueryClient();
  const [section, setSection] = useState<typeof SECTIONS[number]>('ПЗ');
  const [defectKind, setDefectKind] = useState<DefectKind>('S');
  const [severity, setSeverity] = useState<'Critical' | 'High' | 'Medium' | 'Low'>('Medium');
  const [body, setBody] = useState('');
  const [norm, setNorm] = useState('');
  const [specialist, setSpecialist] = useState('');

  const create = useMutation({
    mutationFn: (payload: RemarkCreate) => api.post(`/projects/${projectId}/remarks`, payload),
    onSuccess: () => {
      toast.success('Замечание создано');
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось создать'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!snapshotId) {
      toast.error('Нет активного редакции. Загрузите редакцию документации в табе Документы.');
      return;
    }
    if (!body.trim()) return;
    const payload: RemarkCreate = {
      snapshot_id: snapshotId,
      body: body.trim(),
      severity,
      specialist_role: specialist.trim() || null,
      // Backend сам определит defect_type по аспекту/секции в metadata extractor
    };
    create.mutate(payload);
  };

  return (
    <Modal
      title="Новое замечание"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button variant="primary" onClick={(e) => submit(e as unknown as FormEvent)} disabled={!body.trim() || create.isPending}>
            {create.isPending ? 'Создание…' : 'Создать замечание'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col" style={{ gap: 16 }}>
        <Field label="Раздел документа">
          <select value={section} onChange={(e) => setSection(e.target.value as typeof section)} className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink" style={{ padding: '10px 14px', fontSize: 14 }}>
            {SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <Field label="Тип">
          <Segmented
            value={defectKind}
            onChange={setDefectKind}
            options={[
              { id: 'S', label: 'Блокирующее' },
              { id: 'E', label: 'Устранимое' },
            ]}
          />
        </Field>

        <Field label="Важность">
          <Segmented
            value={severity}
            onChange={setSeverity}
            options={SEVERITY_OPTS.map((s) => ({ id: s.id, label: s.label, dot: s.color }))}
          />
        </Field>

        <Field label="Текст замечания">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink resize-vertical"
            style={{ padding: '10px 14px', fontSize: 14, fontFamily: 'Inter, system-ui, sans-serif' }}
            placeholder="Чёткое описание дефекта с указанием листа/страницы документации"
          />
        </Field>

        <Field label="Цитата нормы (опц.)" hint="Если ссылаетесь на конкретный пункт — вставьте, RAG найдёт в индексе">
          <textarea
            value={norm}
            onChange={(e) => setNorm(e.target.value)}
            rows={2}
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'Inter, system-ui, sans-serif' }}
            placeholder="СП 37.13330.2012, п. 4.3 — Радиус кривой в плане…"
          />
        </Field>

        <Field label="Назначенная роль эксперта (опц.)">
          <input
            type="text"
            value={specialist}
            onChange={(e) => setSpecialist(e.target.value)}
            className="w-full bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 14px', fontSize: 14 }}
            placeholder="Например: геотехника, верх. строение"
          />
        </Field>
      </form>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-muted mt-1" style={{ fontSize: 11 }}>{hint}</p>}
    </div>
  );
}

function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { id: T; label: string; dot?: string }[] }) {
  return (
    <div className="inline-flex bg-surface rounded-pill" style={{ padding: 4, gap: 4 }}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className="rounded-pill flex items-center"
            style={{
              padding: '6px 14px', gap: 6,
              background: active ? '#FFFFFF' : 'transparent',
              border: 'none', cursor: 'pointer',
              boxShadow: active ? '0 1px 3px rgba(10,22,40,.08)' : 'none',
              fontWeight: active ? 600 : 500, fontSize: 13,
              color: active ? '#0A1628' : '#6B7280',
            }}
          >
            {opt.dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.dot }} />}
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
